import { CallState, LoadingState } from '@lcs/shared/api';
import { Action, createReducer, on } from '@ngrx/store';
import { TimeZones } from 'generated/graphql-types';
import { loadAccountDetailsSuccess } from 'src/app/pages/account-details/state/account-detail.actions';
import { accountActiveCallsRegisterAdapterActions } from 'src/app/pages/active-calls/state/account-active-calls/account-active-calls.actions';
import { accountCallHistoryRegisterAdapterActions } from 'src/app/pages/active-calls/state/account-call-history/account-call-history.actions';
import { systemActiveCallsRegisterAdapterActions } from 'src/app/pages/active-calls/state/system-active-calls/system-active-calls.actions';
import { allowedIpRegisterAdapterActions } from 'src/app/pages/allowed-ips-register/state/allowed-ip-register.actions';
import { bannedProvisioningIpRegisterAdapterActions } from 'src/app/pages/banned-provisioning-ip-register/state/banned-provisioning-ip-register.actions';
import { blockedPhoneNumberRegisterAdapterActions } from 'src/app/pages/blocked-phone-number-register-component/state/blocked-phone-number-register.actions';
import { loadDepartmentDetailSuccess } from 'src/app/pages/department-details/state/department-detail.actions';
import { departmentRegisterAdapterActions } from 'src/app/pages/department-register/state/department-register.actions';
import {
   loadDeviceDetailSuccess,
   loadSystemDeviceDetailsSuccess,
} from 'src/app/pages/device-details/state/device-detail.actions';
import {
   loadAccountDevicesSuccess,
   loadSystemDevicesSuccess,
} from 'src/app/pages/device-register/state/device-register.actions';
import { loadExtensionDetailSuccess } from 'src/app/pages/extension-details/state/extension-detail.actions';
import { loadExtensionGroupDetailSuccess } from 'src/app/pages/extension-group-details/state/extension-group-detail.actions';
import { extensionGroupRegisterAdapterActions } from 'src/app/pages/extension-groups-register/state/extension-group-register.actions';
import { extensionRegisterAdapterActions } from 'src/app/pages/extension-register/state/extension-register.actions';
import { incomingFaxRegisterAdapterActions } from 'src/app/pages/fax-register/incoming-fax-register/state/incoming-fax-register.actions';
import { outgoingFaxRegisterAdapterActions } from 'src/app/pages/fax-register/outgoing-fax-register/state/outgoing-fax-register.actions';
import { loadLocationDetailSuccess } from 'src/app/pages/location-details/state/location-detail.actions';
import { locationsRegisterAdapterActions } from 'src/app/pages/locations-register-component/state/locations-register.actions';
import { loadMailboxDetailSuccess } from 'src/app/pages/mailbox-details/state/mailbox-detail.actions';
import { mailboxRegisterAdapterActions } from 'src/app/pages/mailbox-register/state/mailbox-register.actions';
import {
   loadPhoneNumberDetailSuccess,
   loadSystemPhoneNumberDetailSuccess,
} from 'src/app/pages/phone-number-details/state/phone-number-detail.actions';
import {
   loadAccountPhoneNumbersSuccess,
   loadSystemPhoneNumbersSuccess,
} from 'src/app/pages/phone-number-register/state/phone-number-register.actions';
import { loadPromptDetailSuccess } from 'src/app/pages/prompt-details/state/prompt-detail.actions';
import { promptRegisterAdapterActions } from 'src/app/pages/prompt-register/state/prompt-register.actions';
import {
   loadRoleDetailsSuccess,
   loadSystemRoleDetailsSuccess,
} from 'src/app/pages/role-details/state/role-detail.actions';
import {
   loadAccountRolesSuccess,
   loadSystemRolesSuccess,
} from 'src/app/pages/role-register/state/role-register.actions';
import { loadScheduleDetailSuccess } from 'src/app/pages/schedule-details/state/schedule-detail.actions';
import { scheduleRegisterAdapterActions } from 'src/app/pages/schedule-register/state/schedule-register.actions';
import {
   loadAccountLogsSuccess,
   loadSystemLogsSuccess,
} from 'src/app/pages/system-log-register/state/system-log-register.actions';
import {
   loadSystemUserDetailsSuccess,
   loadUserDetailsSuccess,
} from 'src/app/pages/user-details/state/user-detail.actions';
import {
   loadAccountUsersSuccess,
   loadSystemUsersSuccess,
} from 'src/app/pages/user-register/state/user-register.actions';
import * as SiteLayoutActions from '../../pages/site-layout/state/site-layout.actions';
import * as AccountRegisterActions from './../../pages/account-register/state/account-register.actions';
import * as SelectAccountActions from './select-account.actions';

export const selectAccountFeatureKey = 'selectAccountCore';

export const accountDetailSpecificActions = [
   loadExtensionDetailSuccess,
   loadPhoneNumberDetailSuccess,
   loadPromptDetailSuccess,
   loadUserDetailsSuccess,
   loadDeviceDetailSuccess,
   loadScheduleDetailSuccess,
   loadLocationDetailSuccess,
   loadRoleDetailsSuccess,
   loadExtensionGroupDetailSuccess,
   loadDepartmentDetailSuccess,
];

export const accountRegisterSpecificActions = [
   extensionRegisterAdapterActions.loadSuccess,
   incomingFaxRegisterAdapterActions.loadSuccess,
   outgoingFaxRegisterAdapterActions.loadSuccess,
   loadAccountUsersSuccess,
   loadAccountPhoneNumbersSuccess,
   loadAccountDevicesSuccess,
   loadAccountRolesSuccess,
   loadAccountLogsSuccess,
   locationsRegisterAdapterActions.loadSuccess,
   scheduleRegisterAdapterActions.loadSuccess,
   mailboxRegisterAdapterActions.loadSuccess,
   promptRegisterAdapterActions.loadSuccess,
   blockedPhoneNumberRegisterAdapterActions.loadSuccess,
   extensionGroupRegisterAdapterActions.loadSuccess,
   accountCallHistoryRegisterAdapterActions.loadSuccess,
   accountActiveCallsRegisterAdapterActions.loadSuccess,
   departmentRegisterAdapterActions.loadSuccess,
];

export const systemRegisterActions = [
   loadSystemDevicesSuccess,
   loadSystemPhoneNumbersSuccess,
   loadSystemUsersSuccess,
   loadSystemRolesSuccess,
   loadSystemLogsSuccess,
   systemActiveCallsRegisterAdapterActions.loadSuccess,
   bannedProvisioningIpRegisterAdapterActions.loadSuccess,
   allowedIpRegisterAdapterActions.loadSuccess,
];

export const systemDetailActions = [
   loadSystemUserDetailsSuccess,
   loadSystemPhoneNumberDetailSuccess,
   loadSystemDeviceDetailsSuccess,
   loadSystemRoleDetailsSuccess,
];

export interface State {
   accountId: number | null;
   accountName: string | null;
   accountShortName: string | null;
   accountCallState?: CallState;
   isOnSystemPage?: boolean;
   timeZoneId: TimeZones | null;
}

export const initialState: State = {
   accountCallState: LoadingState.Init,
   accountId: null,
   accountName: null,
   accountShortName: null,
   isOnSystemPage: false,
   timeZoneId: null,
};

const selectAccountReducer = createReducer(
   initialState,
   on(SelectAccountActions.selectedAccountLoaded, (state, { accountId, accountName, accountShortName, timeZoneId }) => {
      return {
         ...state,
         accountCallState: LoadingState.Loaded,
         accountId: accountId,
         accountName: accountName,
         accountShortName: accountShortName,
         isOnSystemPage: accountId == null,
         timeZoneId: timeZoneId,
      };
   }),
   on(
      SelectAccountActions.selectedAccountLoadedOnLogin,
      (state, { accountId, accountName, accountShortName, isSystemUser, timeZoneId }) => {
         return {
            ...state,
            accountCallState: LoadingState.Loaded,
            accountId: accountId,
            accountName: accountName,
            accountShortName: accountShortName,
            isOnSystemPage: isSystemUser,
            timeZoneId: timeZoneId,
         };
      }
   ),
   on(...accountDetailSpecificActions, (state, { data }) => {
      return {
         accountCallState: LoadingState.Loaded,
         accountId: data?.account?.accountId ?? null,
         accountName: data?.account?.name ?? null,
         accountShortName: state.accountShortName,
         isOnSystemPage: false,
         timeZoneId: state.timeZoneId,
      };
   }),
   on(...accountRegisterSpecificActions, (state, action) => {
      return {
         ...state,
         isOnSystemPage: false,
      };
   }),
   on(loadAccountDetailsSuccess, (state, { data }) => {
      return {
         accountCallState: LoadingState.Loaded,
         accountId: data.accountId,
         accountName: data.name,
         accountShortName: data.shortName,
         isOnSystemPage: false,
         timeZoneId: data.timeZoneId,
         extensions: data.extensionsConnection.items,
         extensionGroups: data.extensionGroupsConnection.items,
      };
   }),
   on(SiteLayoutActions.updateAccount, (state, { accountId, name }) => {
      return {
         ...state,
         accountCallState: LoadingState.Loaded,
         accountId: accountId,
         accountName: name,
         accountShortName: null,
      };
   }),
   on(AccountRegisterActions.accountRegisterAdapterActions.pageEnter, (state) => {
      return {
         accountCallState: LoadingState.Loaded,
         ...state,
         isOnSystemPage: true,
      };
   }),
   on(SelectAccountActions.accountRoutingChange, (state) => {
      return {
         accountCallState: LoadingState.Loading,
         ...state,
      };
   }),
   on(...systemRegisterActions, (state) => {
      return {
         ...state,
         accountCallState: LoadingState.Loaded,
         isOnSystemPage: true,
         accountId: null,
      };
   }),
   on(...systemDetailActions, (state, { data }) => {
      return {
         ...state,
         accountCallState: LoadingState.Loaded,
         accountId: data?.account?.accountId ?? null,
         accountName: data?.account?.name ?? null,
         accountShortName: state.accountShortName,
         isOnSystemPage: true,
         timeZoneId: state.timeZoneId,
      };
   }),
   on(loadMailboxDetailSuccess, (state, { data }) => {
      const account = data.extension?.account ?? null;

      return {
         ...state,
         accountCallState: LoadingState.Loaded,
         accountId: account?.accountId ?? null,
         accountName: account?.name ?? null,
         isOnSystemPage: false,
      };
   })
);

export function reducer(state: State | undefined, action: Action) {
   return selectAccountReducer(state, action);
}
